import { useMemo } from "react";
import {
  widgetViewPolicy,
  type WidgetViewAcl,
  type WidgetViewTier,
  type WidgetViewWriteGrant,
} from "#app/widgets/dashboards/widget-view-policy";
import { useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { usePermission, useViewerIsShareOnly } from "#app/lib/hooks/use-permission";
import { scopeEntityId, scopeKey, type IssueScope } from "#app/lib/filters/issue-scope";
import { useViewsOptional } from "#app/lib/views/view-context";

/**
 * The client half of the widget view-ownership model. The decision itself is
 * `widgetViewPolicy` (pure, over the SAME shared grammar the server guard
 * feeds), so the edit affordances shown here and what the API will actually
 * accept can't drift — the notes `viewerCanEdit` precedent. This hook only
 * resolves the actor inputs from client data: the `widgets.manage`
 * permission, the current user id (owner compare — `/my-work` always renders
 * the current user), the server-computed `Team.viewerCanManage` from the
 * workspace `teamMap`, and — for a workspace dashboard — the active view
 * row's own ACL (`createdBy` + `viewerAccessLevel`), which the server resolved
 * from the same row and shares the board's writes are checked against.
 */

export type { WidgetViewTier };

// The dashboard's view keys — persisted VALUES ("widget-view-workflow:<id>",
// "widget-view-member:<id>", …); renaming them orphans existing dashboards.
// Optional variants suffix the key so two views can share one scope (a team's
// Overview vs its Dashboard tab).
export function viewFor(scope: IssueScope, variant?: string): string {
  const base = `widget-view-${scopeKey(scope)}`;
  return variant ? `${base}-${variant}` : base;
}

export type WidgetViewAccessState = {
  /** The persisted view key — `viewFor(scope, variant)`. */
  view: string;
  /** Whether writes to the canonical board will be accepted by the server. */
  canWrite: boolean;
  /** WHICH grant made the viewer a shared-board editor ("team-manager" |
   *  "permission" | "owner" | "access-level") — copy-level context for edit
   *  affordances; null for non-editors. */
  writeGrant: WidgetViewWriteGrant | null;
  tier: WidgetViewTier;
  /** A share-only viewer on a shared board reads it and nothing more — the
   *  host renders the widgets with no fork, no edit chrome and no transient
   *  controls. */
  consumeOnly: boolean;
};

export function useWidgetViewAccess(
  scope: IssueScope,
  variant?: string,
  /**
   * A persisted key to render instead of the scope-derived one. A saved
   * dashboard view carries the key the SERVER derived for its row, so the
   * client renders the board the API will accept writes to rather than
   * re-deriving one beside it.
   */
  viewKey?: string,
): WidgetViewAccessState {
  const canManageWidgets = usePermission("widgets.manage");
  const shareOnly = useViewerIsShareOnly();
  const { currentUser } = useAppData();
  const { teamMap } = useWorkspaceData();
  const userId = currentUser?.userId ?? "";
  const entityId = scopeEntityId(scope);
  // Only a WORKSPACE-scoped view row carries its own ACL; a workflow's saved
  // dashboard views stay on the shared `widgets.manage` rule.
  const activeView = useViewsOptional()?.activeView ?? null;
  const aclOwnerId =
    activeView?.scopeKind === "workspace" ? (activeView.createdBy ?? null) : null;
  const aclLevel =
    activeView?.scopeKind === "workspace" ? (activeView.viewerAccessLevel ?? null) : null;
  const hasAcl = activeView?.scopeKind === "workspace";

  return useMemo(() => {
    const view = viewKey ?? viewFor(scope, variant);
    const viewAcl: WidgetViewAcl | undefined = hasAcl
      ? { ownerId: aclOwnerId, viewerAccessLevel: aclLevel }
      : undefined;
    const policy = widgetViewPolicy(
      view,
      {
        userId,
        canManageWidgets,
        managesTeam: (teamId) => teamMap.get(teamId)?.viewerCanManage === true,
        shareOnly,
      },
      viewAcl,
    );
    return { view, ...policy };
    // Memoize on primitives (hosts re-create the scope object per render —
    // the dashboard's entityScope memo precedent).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the scope primitives, not the scope object
  }, [
    scope.kind,
    entityId,
    variant,
    viewKey,
    userId,
    canManageWidgets,
    shareOnly,
    teamMap,
    hasAcl,
    aclOwnerId,
    aclLevel,
  ]);
}
